import { IDriverset } from '../api/models';

// Must have ts-ignore, because there are no types.
// @ts-ignore
import createAquafishModule from './aquafish.mjs';

import { AquaFishModel } from './AquaFishModel';
import { interpolateDriverSet } from './Interpolation';

enum AquaFishCulture {
  SEAWATERCULTURE   = 0,
  FRESHWATERCULTURE = 1 
}

interface IAquaFishInitParams {
  initialWeight?: number; // g     
  foodMass?: number;           
  timeStep?: number;           
  boxVolume?: number;          
  numAnimals?: number;     
  feedToSatiation?: boolean;
  feedOnBiomass?: boolean;  
  feedLossCoefficient?: number;
  culture?: AquaFishCulture; 
  /** An AquaFishModel value. Typed as number because codes reach us from the
   *  species table, not from the enum. */
  species?: number;
  startTime?: number;
  endTime?: number;  
}

const DefaultAquaFishInitParams: IAquaFishInitParams = {
  initialWeight: 80,
  foodMass: 50.0,                     
  timeStep: 1.0,                      
  boxVolume: 1.0,                     
  numAnimals: 1,                      
  feedToSatiation: true,              
  feedOnBiomass: false,               
  feedLossCoefficient: 0.05,          
  culture: AquaFishCulture.SEAWATERCULTURE, 
  species: AquaFishModel.AQUAFISHFTATLANTICSALMON, 
  startTime: 0,
  endTime: 10  
}

interface IAquaFishDay {
  /** Elapsed culture day, 0-based. Not the model's internal clock, which
   *  starts at AStartTime — see the conversion below. */
  modelTime: number;
  individualWeight: number;
  individualLength: number;
  FCR: number;
  feedSupply: number;
  feedIngestion: number;
  wasteFeed: number;
  faecesElimination: number;    
}

interface IAquaFishResult {
  totalFeedSupplied: number;
  totalFeedIngestion: number;
  totalWasteFeed: number;
  totalFaeces: number;
  totalOxygenConsumption: number;
  totalAmmoniaExcretion: number;
  days: IAquaFishDay[];
}

class AquaFishFacade {

  static call = async (initParams: IAquaFishInitParams, driverset: IDriverset): Promise<IAquaFishResult> => {
    const aquafish = await createAquafishModule();

    const interpolation = interpolateDriverSet(driverset);

    // Create and initialize AquaFishInitParams
    const _initParams = new aquafish.AquaFishInitParams();
    _initParams.AInitialWeight = initParams.initialWeight ?? DefaultAquaFishInitParams.initialWeight;
    _initParams.AFoodMass = initParams.foodMass ?? DefaultAquaFishInitParams.foodMass;   
    _initParams.ATimeStep = initParams.timeStep ?? DefaultAquaFishInitParams.timeStep;   
    _initParams.ABoxVolume = initParams.boxVolume ?? DefaultAquaFishInitParams.boxVolume;
    _initParams.ANumberOfAnimals = initParams.numAnimals ?? DefaultAquaFishInitParams.numAnimals;
    _initParams.AFeedToSatiation = initParams.feedToSatiation ?? DefaultAquaFishInitParams.feedToSatiation;
    _initParams.AFeedOnBiomass = initParams.feedOnBiomass ?? DefaultAquaFishInitParams.feedOnBiomass;
    _initParams.AFeedLossCoefficient = initParams.feedLossCoefficient ?? DefaultAquaFishInitParams.feedLossCoefficient;
    _initParams.ATypeOfCulture = initParams.culture ?? DefaultAquaFishInitParams.culture;
    _initParams.ASpeciesCode = initParams.species ?? DefaultAquaFishInitParams.species;
    _initParams.AStartTime = initParams.startTime ?? DefaultAquaFishInitParams.startTime;
    _initParams.AEndTime = initParams.endTime ?? DefaultAquaFishInitParams.endTime;      

    const temperatureArray = new aquafish.VectorDouble();
    const salinityArray = new aquafish.VectorDouble();
    const chlorophyllArray = new aquafish.VectorDouble();
    const currentSpeedArray = new aquafish.VectorDouble();
    const dissolvedOxygenArray = new aquafish.VectorDouble();

    // FinFish.cpp/ShellFish.cpp treat AEndTime as a *duration* (`runTime`), not an
    // absolute end: the model loops modelTime from AStartTime to
    // AStartTime + AEndTime inclusive, stepping by ATimeStep, and consumes one
    // driver value per iteration. The iteration count is therefore independent of
    // AStartTime. Mirror that loop exactly here so the arrays are always the length
    // the model reads; sizing them as (AEndTime - AStartTime + 1) only happened to
    // work while AStartTime was always 0, and would under-supply the model — an
    // unchecked out-of-bounds vector read in WASM — for any later start day.
    //
    // idx walks the interpolated year from the culture start day, wrapping at the
    // 365-day boundary. AStartTime is a 0-based day-of-year (0 = 1 January), which
    // is the same basis interpolateDriverSet uses, so it doubles as the offset.
    // Only rows 0..364 are read; interpolateDriverSet's 366th row duplicates row 0
    // and is not needed here.
    let i = 0;
    for (let modelTime = _initParams.AStartTime;
         modelTime <= _initParams.AStartTime + _initParams.AEndTime;
         modelTime += _initParams.ATimeStep) {
      const idx = (_initParams.AStartTime + i) % 365;
      temperatureArray.push_back(interpolation[idx].waterTemperature);
      salinityArray.push_back(interpolation[idx].salinity);
      chlorophyllArray.push_back(interpolation[idx].chlorophyll);
      currentSpeedArray.push_back(interpolation[idx].currentSpeed);
      dissolvedOxygenArray.push_back(interpolation[idx].dissolvedOxygen);
      i++;
    }

    const result = aquafish.runAquafishModel(_initParams, 
      temperatureArray, salinityArray, chlorophyllArray, currentSpeedArray, dissolvedOxygenArray);

    // Convert results to plain JS array:
    const days: IAquaFishDay[] = [];
    for (let i = 0; i < result.results.size(); i++) {
      const r = result.results.get(i);
      days.push({
        // The model's ModelTime runs from AStartTime, so a culture starting in
        // June reports 151..550 rather than 0..399. Callers plot and index this
        // as elapsed days, so rebase it here and keep AStartTime internal.
        modelTime: r.ModelTime - _initParams.AStartTime,
        individualWeight: r.IndividualWeight,
        individualLength: r.IndividualLength,
        FCR: r.FCR,
        feedSupply: r.FeedSupply,
        feedIngestion: r.FeedIngestion,
        wasteFeed: r.WasteFeed,
        faecesElimination: r.FaecesElimination,
      });
    }

    const aquafishresult: IAquaFishResult = {
      totalFeedSupplied: result.summary.totalFeedSupplied,
      totalFeedIngestion: result.summary.totalFeedIngestion,
      totalWasteFeed: result.summary.totalWasteFeed,
      totalFaeces: result.summary.totalFaeces,
      totalOxygenConsumption: result.summary.totalOxygenConsumption,
      totalAmmoniaExcretion: result.summary.totalAmmoniaExcretion,
      days: days
    }

    return aquafishresult;
  }

}

export { AquaFishFacade, IAquaFishResult }
